import { Checkbox, FormControlLabel, FormGroup, CircularProgress, TextField } from "@mui/material";
import TableFilter from "components/TableFilter/TableFilter";
import { capitalCase } from "change-case";
import { useState } from "react";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDAlert from "components/MDAlert";
import { equals, reject, uniq } from "ramda";
import parseGraphQLError from "utils/graphQL/parseGraphQLError";

export default function MultipleChoiceFilter<T extends string | number>({
  id,
  value,
  options,
  loading,
  error,
  onChange,
  search,
  onOpen,
  onClose,
  onEndReached,
  hasMorePages = false,
}: {
  id: string;
  value: T[];
  options: { label: string; value: T }[];
  loading?: boolean;
  error?: any;
  onChange: (value: T[]) => void;
  search?: (value: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onEndReached?: () => void;
  hasMorePages?: boolean;
}) {
  const [selectedOptions, setSelectedOptions] = useState<T[]>(value ?? []);

  const handleOnchange = (optionId, checked) => {
    setSelectedOptions((prevState) =>
      checked ? uniq([...prevState, optionId]) : reject(equals(optionId), prevState)
    );
  };

  const clearFilter = () => {
    setSelectedOptions([]);
  };

  return (
    <>
      <TableFilter value={value} id={id} onOpen={onOpen} onClose={onClose}>
        {error && (
          <MDBox sx={{ maxWidth: "250px" }}>
            <MDAlert color="error">{parseGraphQLError(error)}</MDAlert>
          </MDBox>
        )}
        {!error && (
          <>
            {search && (
              <TextField
                fullWidth
                size="small"
                placeholder="Search companies"
                onChange={(event) => search(event.target.value)}
                sx={{ mb: 1 }}
              />
            )}
            {loading && options.length === 0 ? (
              <MDBox display="flex" justifyContent="center" py={2}>
                <CircularProgress color="inherit" size="18px" />
              </MDBox>
            ) : (
              <div
                className="multi-select"
                onScroll={(event) => {
                  const list = event.currentTarget;
                  if (
                    hasMorePages &&
                    list.scrollTop + list.clientHeight >= list.scrollHeight - 32
                  ) {
                    onEndReached?.();
                  }
                }}
              >
                <FormGroup>
                  {options.map((choice) => (
                    <FormControlLabel
                      key={choice.value}
                      control={
                        <Checkbox
                          checked={selectedOptions?.includes(choice.value) ?? false}
                          onChange={(e, checked) => handleOnchange(choice.value, checked)}
                        />
                      }
                      label={capitalCase(choice.label)}
                    />
                  ))}
                </FormGroup>
              </div>
            )}
            {loading && options.length > 0 && (
              <MDBox display="flex" justifyContent="center" py={1}>
                <CircularProgress color="inherit" size="14px" />
              </MDBox>
            )}

            <MDButton
              type="button"
              variant="gradient"
              color="secondary"
              onClick={() => {
                clearFilter();
              }}
              sx={{ margin: "10px" }}
            >
              Clear
            </MDButton>
            <MDButton type="button" color="blue" onClick={() => onChange(selectedOptions)}>
              Apply
            </MDButton>
          </>
        )}
      </TableFilter>
    </>
  );
}
